<script setup lang="ts">
  import { onBeforeUnmount, ref } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useModal } from '@/composition/use-modal';
  import { MENU_UPDATE_UNDO_MODAL } from '@/domain/menu-import/update-undo';
  import type { TUndoUpdateAttempt } from '@/domain/menu-import/update-undo';

  /**
   * «¿Deshacer la actualización?» (plan §3.9): what an undo puts back, and
   * that later changes stay. «Deshacer» awaits `confirm()` with its own
   * busy state; a refused undo stays here (the error was toasted), anything
   * else closes the dialog with its result
   * (`showDynamicDialog<TUndoUpdateAttempt | null>`). Nothing is touched
   * after it closes.
   */
  const props = defineProps<{
    confirm: () => Promise<TUndoUpdateAttempt>;
  }>();
  const emit = defineEmits<{ (e: 'close'): void }>();
  const modals = useModal();

  const busy = ref(false);
  let alive = true;

  async function undo() {
    if (busy.value) return;
    busy.value = true;
    const attempt = await props.confirm();
    if (!alive) return;
    if (attempt === 'failed') {
      busy.value = false;
      return;
    }
    // Closes the dialog: nothing local is touched after this.
    void modals.dialogSubmit(MENU_UPDATE_UNDO_MODAL, attempt);
  }

  function cancel() {
    if (!busy.value) emit('close');
  }

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <div class="v2-menu-update-undo-dialog">
    <header class="dialog-head">
      <h2 class="dialog-title">{{ $t('menuImport.update.undoTitle') }}</h2>
    </header>
    <p class="dialog-text">{{ $t('menuImport.update.undoText') }}</p>
    <footer class="dialog-foot">
      <PosUiButton
        color="neutral"
        variant="outline"
        :disabled="busy"
        @click="cancel"
        >{{ $t('menuImport.update.undoCancel') }}</PosUiButton
      >
      <!-- busy stays on until the dialog closes: the spinner never flips during its teardown. -->
      <PosUiButton color="error" variant="soft" :loading="busy" @click="undo">{{
        $t('menuImport.undo')
      }}</PosUiButton>
    </footer>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-update-undo-dialog {
    @apply tw-flex tw-flex-col tw-gap-3 tw-bg-white tw-px-5 tw-pb-5 tw-pt-5;

    .dialog-head {
      @apply tw-flex;
    }
    .dialog-title {
      @apply tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
    }
    .dialog-text {
      @apply tw-m-0 tw-text-sm tw-text-neutral-700;
    }
    .dialog-foot {
      @apply tw-flex tw-flex-wrap tw-justify-end tw-gap-2 tw-pt-2;
    }
  }

  // Phones: the two buttons share the row, «Deshacer» last.
  @media (max-width: 479px) {
    .v2-menu-update-undo-dialog .dialog-foot > * {
      @apply tw-flex-1;
    }
  }
</style>
